(() => {
  if (window.PuraGameDayConfigurator?.initialize) {
    window.PuraGameDayConfigurator.initialize(document);
    return;
  }
  const SELECTOR = '[data-game-day-configurator]';
  const money = (cents) => {
    const amount = (cents || 0) / 100;
    const fractionDigits = Number.isInteger(amount) ? 0 : 2;
    return new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: window.Shopify?.currency?.active || 'USD', minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits }).format(amount);
  };
  const safeUrl = (url) => { try { return new URL(url, document.baseURI).toString(); } catch (_) { return ''; } };
  const imageUrl = (url, width) => { try { const image = new URL(url, document.baseURI); image.searchParams.set('width', width); return image.toString(); } catch (_) { return safeUrl(url); } };
  const normalizeSilhouette = (value) => String(value || '').trim().toLowerCase();
  const BUTTON_STATES = {
    SELECT_SIZE: 'SELECT_SIZE',
    ADD_TO_CART: 'ADD_TO_CART',
    PRE_ORDER: 'PRE_ORDER',
    NOTIFY_ME: 'NOTIFY_ME',
    UNAVAILABLE: 'UNAVAILABLE'
  };

  // Mirrors the PDP contract exposed by PuraCashmerePreOrder on product pages.
  // The Game Day page does not load ajax-pdp.js, so its product payload is adapted below.
  const formatPreOrderProperty = (dispatchDate) => {
    if (!dispatchDate || dispatchDate === 'TBD') return '**Pre-Order: Ships TBD';
    const date = new Date(dispatchDate);
    if (Number.isNaN(date.getTime())) return '**Pre-Order: Ships TBD';
    return `**Pre-Order: Ships ${date.toLocaleDateString('en-US', { month: 'short' })}. ${date.getDate()}`;
  };
  const isPreOrderActive = (variantData) => {
    if (!variantData || variantData.show_pre_order === false) return false;
    const inventory = variantData.inventory || 0;
    const maxPreOrders = variantData.max_pre_orders || 0;
    return inventory <= 0 && inventory > maxPreOrders && Boolean(variantData.available);
  };
  const computeButtonState = (variantData, { hasSelectedSize } = {}) => {
    if (!hasSelectedSize) return { state: BUTTON_STATES.SELECT_SIZE, disabled: true, preOrderProperty: null };
    if (!variantData) return { state: BUTTON_STATES.UNAVAILABLE, disabled: true, preOrderProperty: null };
    if (variantData.show_pre_order === false) {
      return variantData.available && (variantData.inventory || 0) > 0
        ? { state: BUTTON_STATES.ADD_TO_CART, disabled: false, preOrderProperty: null }
        : { state: BUTTON_STATES.NOTIFY_ME, disabled: false, preOrderProperty: null };
    }
    if (isPreOrderActive(variantData)) return { state: BUTTON_STATES.PRE_ORDER, disabled: false, preOrderProperty: formatPreOrderProperty(variantData.dispatch_date) };
    if ((variantData.inventory || 0) > 0 && variantData.available) return { state: BUTTON_STATES.ADD_TO_CART, disabled: false, preOrderProperty: null };
    return { state: BUTTON_STATES.NOTIFY_ME, disabled: false, preOrderProperty: null };
  };

  class GameDayConfigurator {
    constructor(root) {
      if (root.__gameDayConfigurator) return root.__gameDayConfigurator;
      root.__gameDayConfigurator = this;
      this.root = root;
      this.data = JSON.parse(root.querySelector('[data-gd-data]').textContent);
      this.nodes = {
        pairs: root.querySelector('[data-gd-step="pair"]'), silhouettes: root.querySelector('[data-gd-step="silhouette"]'), sizes: root.querySelector('[data-gd-step="size"]'),
        silhouetteList: root.querySelector('.game-day-configurator__silhouettes'), sizeList: root.querySelector('.game-day-configurator__sizes'), gallery: root.querySelector('[data-gd-gallery]'), fitBar: root.querySelector('[data-gd-fit-bar]'),
        pairLabel: root.querySelector('[data-gd-pair-label]'), title: root.querySelector('[data-gd-product-title]'), price: root.querySelector('[data-gd-price]'), availability: root.querySelector('[data-gd-availability]'), add: root.querySelector('[data-gd-add]'), mobileAdd: root.querySelector('[data-gd-mobile-add]'), mobileBar: root.querySelector('[data-gd-mobile-bar]'), mobilePrice: root.querySelector('[data-gd-mobile-price]'), pdpLink: root.querySelector('[data-gd-pdp-link]'), error: root.querySelector('[data-gd-error]')
      };
      const query = new URLSearchParams(window.location.search);
      const deepPair = this.data.pairs.find((pair) => pair.slug === query.get('pair'));
      this.preference = normalizeSilhouette(query.get('sil'));
      const initialPair = deepPair || this.data.pairs[0];
      const requestedSilhouette = normalizeSilhouette(query.get('sil'));
      const deepSilhouette = deepPair?.products.find((product) => normalizeSilhouette(product.silhouette) === requestedSilhouette)?.silhouette || null;
      this.state = { pairId: initialPair?.id || null, silhouette: deepSilhouette || this.resolveSilhouette(initialPair, this.preference), size: null, committed: { pair: Boolean(deepPair), silhouette: Boolean(deepSilhouette), size: false }, editing: null };
      this.selectionRevision = 0;
      this.activeAddRequestId = 0;
      this.onClick = this.handleClick.bind(this);
      this.onKeydown = this.handleKeydown.bind(this);
      this.onResize = () => { this.applyMobileFolding(); this.syncMobileBar(); };
      this.bind();
      this.render();
    }

    get pair() { return this.data.pairs.find((pair) => pair.id === this.state.pairId); }
    get product() { return this.pair?.products.find((product) => product.silhouette === this.state.silhouette); }
    get sizeIndex() { const index = this.product?.optionNames?.findIndex((name) => /size/i.test(name)); return index >= 0 ? index : Math.max(0, (this.product?.variants?.[0]?.options?.length || 1) - 1); }
    get variant() {
      const matches = this.product?.variants.filter((variant) => variant.options[this.sizeIndex] === this.state.size) || [];
      return matches.length === 1 ? matches[0] : null;
    }
    get canonicalVariant() {
      const variant = this.variant;
      if (!variant) return null;
      const product = this.product || {};
      return {
        available: Boolean(variant.available),
        inventory: Number(variant.inventoryQuantity || 0),
        max_pre_orders: Number(variant.maxPreOrders ?? product.maxPreOrders ?? -500),
        dispatch_date: variant.dispatchDate || product.dispatchDate || 'TBD',
        show_pre_order: product.showPreOrder === false ? false : true
      };
    }
    isViable(product) { return product?.variants?.some((variant) => variant.available) || false; }

    resolveSilhouette(pair, preference = '') {
      const products = pair?.products || [];
      const preferred = products.find((product) => normalizeSilhouette(product.silhouette) === normalizeSilhouette(preference) && this.isViable(product));
      return (preferred || products.find((product) => this.isViable(product)) || products[0])?.silhouette || null;
    }

    setSilhouettePreference(preference) {
      this.preference = normalizeSilhouette(preference);
      const silhouette = this.resolveSilhouette(this.pair, this.preference);
      if (silhouette !== this.state.silhouette) {
        this.selectionRevision += 1;
        this.state.silhouette = silhouette;
        this.state.size = null;
        this.state.committed.silhouette = false;
        this.state.committed.size = false;
      }
      this.state.editing = null;
      this.updateUrl();
      this.render();
    }

    bind() {
      this.root.addEventListener('click', this.onClick);
      this.root.addEventListener('keydown', this.onKeydown);
      window.addEventListener('resize', this.onResize, { passive: true });
      if ('IntersectionObserver' in window) {
        this.visibilityObserver = new IntersectionObserver((entries) => {
          this.root.dataset.gameDayInView = String(entries.some((entry) => entry.isIntersecting));
          this.syncMobileBar();
        }, { threshold: 0.01 });
        this.visibilityObserver.observe(this.root);
      }
    }

    handleClick(event) {
        const pair = event.target.closest('[data-gd-pair]');
        const silhouette = event.target.closest('[data-gd-silhouette]');
        const size = event.target.closest('[data-gd-size]');
        if (pair) {
          this.selectionRevision += 1;
          const nextPair = this.data.pairs.find((candidate) => candidate.id === pair.dataset.gdPair);
          this.state.pairId = nextPair?.id || this.state.pairId;
          this.state.silhouette = this.resolveSilhouette(nextPair, this.preference);
          this.state.size = null;
          this.state.committed = { pair: true, silhouette: false, size: false };
          this.state.editing = null;
          this.updateUrl();
          this.render();
          this.advance(this.nodes.silhouettes);
          return;
        }
        if (silhouette) {
          this.selectionRevision += 1;
          this.state.silhouette = silhouette.dataset.gdSilhouette;
          this.state.size = null;
          this.state.committed.silhouette = true;
          this.state.committed.size = false;
          this.state.editing = null;
          this.updateUrl();
          this.render();
          this.advance(this.nodes.sizes);
          return;
        }
        if (size) {
          this.selectionRevision += 1;
          this.state.size = size.dataset.gdSize;
          this.state.committed.size = true;
          this.state.editing = null;
          this.render();
          this.advance(this.nodes.gallery || this.nodes.sizes);
          return;
        }
        const galleryTrigger = event.target.closest('[data-gd-gallery-index]');
        if (galleryTrigger) {
          this.selectGalleryMedia(Number(galleryTrigger.dataset.gdGalleryIndex));
          return;
        }
        const sizeChart = event.target.closest('[aria-controls^="game-day-size-chart-"]');
        if (sizeChart) { document.getElementById(sizeChart.getAttribute('aria-controls'))?.show?.(); return; }
        const change = event.target.closest('[data-game-day-change]');
        if (change) {
          const stage = change.dataset.gameDayChange;
          this.state.editing = stage;
          this.applyMobileFolding();
          return;
        }
        const mobileAdd = event.target.closest('[data-gd-mobile-add]');
        if (mobileAdd && this.nodes.add.classList.contains('my-bis-button')) { this.nodes.add.click(); return; }
        if (event.target.closest('[data-gd-add]') || mobileAdd) this.add();
    }

    handleKeydown(event) {
        if (!['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft'].includes(event.key)) return;
        const group = event.target.closest('[role="radiogroup"]');
        const choices = group ? Array.from(group.querySelectorAll('[role="radio"]:not([disabled])')) : [];
        if (!choices.includes(event.target)) return;
        event.preventDefault();
        const direction = event.key === 'ArrowDown' || event.key === 'ArrowRight' ? 1 : -1;
        const next = choices[(choices.indexOf(event.target) + direction + choices.length) % choices.length];
        next.focus();
        next.click();
    }

    destroy() {
      this.root.removeEventListener('click', this.onClick);
      this.root.removeEventListener('keydown', this.onKeydown);
      window.removeEventListener('resize', this.onResize);
      this.visibilityObserver?.disconnect();
      delete this.root.__gameDayConfigurator;
    }

    updateUrl() {
      const url = new URL(window.location.href);
      if (this.state.committed.silhouette && this.state.silhouette) {
        url.searchParams.set('pair', this.pair.slug);
        url.searchParams.set('sil', normalizeSilhouette(this.state.silhouette));
      } else if (this.preference) {
        url.searchParams.delete('pair');
        url.searchParams.set('sil', this.preference);
      } else {
        url.searchParams.delete('pair');
        url.searchParams.delete('sil');
      }
      window.history.replaceState({}, '', url);
    }

    syncMobileBar() {
      if (!this.nodes.mobileBar) return;
      this.nodes.mobilePrice.textContent = this.nodes.price.textContent || '--';
      this.nodes.mobileAdd.textContent = this.nodes.add.textContent;
      this.nodes.mobileAdd.disabled = this.nodes.add.disabled;
      this.nodes.mobileAdd.setAttribute('aria-disabled', this.nodes.add.getAttribute('aria-disabled'));
      this.nodes.mobileAdd.setAttribute('aria-busy', this.nodes.add.getAttribute('aria-busy') || 'false');
      this.nodes.mobileAdd.dataset.buttonState = this.nodes.add.dataset.buttonState || '';
      this.nodes.mobileAdd.classList.toggle('my-bis-button', this.nodes.add.classList.contains('my-bis-button'));
      const mobile = window.matchMedia('(max-width: 700px)').matches;
      this.nodes.mobileBar.hidden = !mobile;
    }

    renderFitBar(product) {
      const scale = Number(product?.fitScale);
      if (!Number.isFinite(scale)) return '';
      const position = Math.max(3, Math.min(97, scale));
      const labels = ['Runs Small', 'Slightly Small', 'True to Size', 'Slightly Generous', 'Runs Generous'];
      const active = scale < 13 ? 0 : scale < 38 ? 1 : scale < 63 ? 2 : scale < 88 ? 3 : 4;
      return `<div class="game-day-configurator__fit-bar"><div class="game-day-configurator__fit-head"><span>How it fits</span><span>Based on how customers say it fits</span></div><div class="game-day-configurator__fit-track" aria-hidden="true"><i style="left:${position}%"></i></div><div class="game-day-configurator__fit-labels">${labels.map((label, index) => `<span${index === active ? ' class="is-active"' : ''}>${label}</span>`).join('')}</div></div>`;
    }

    render() {
      const pair = this.pair;
      if (!pair) return;
      this.root.querySelectorAll('[data-gd-pair]').forEach((button) => { const selected = button.dataset.gdPair === pair.id; button.classList.toggle('is-selected', selected); button.setAttribute('aria-checked', String(selected)); button.tabIndex = selected ? 0 : -1; });
      this.nodes.silhouetteList.innerHTML = pair.products.map((product) => {
        const mediaFallback = product.media?.find((media) => media.type === 'image' && media.src);
        const detectedFlatlay = product.media?.find((media) => media.type === 'image' && media.src && /flat[\s_-]*lay|flatlay|laid\s+flat|^cashmere\s+(?:hoodie|contrast\s+crew)/i.test(`${media.alt || ''} ${media.src}`));
        const preview = product.flatlay || detectedFlatlay || mediaFallback || product.featuredImage || product.images?.[0];
        const representativeVariant = product.variants?.find((variant) => variant.available) || product.variants?.[0];
        const selected = product.silhouette === this.state.silhouette;
        const previewMarkup = preview?.src
          ? `<span class="game-day-configurator__silhouette-media"><img src="${this.escape(imageUrl(preview.src, 320))}" alt="${this.escape(preview.alt || `${pair.title} ${product.silhouette} flat lay`)}" width="320" height="400" loading="lazy"></span>`
          : '';
        return `<button type="button" class="game-day-configurator__silhouette ${selected ? 'is-selected' : ''}" data-gd-silhouette="${this.escape(product.silhouette)}" data-game-day-silhouette="${this.escape(product.silhouette)}" role="radio" aria-checked="${selected}" tabindex="${selected ? '0' : '-1'}">${previewMarkup}<span class="game-day-configurator__silhouette-copy"><span class="game-day-configurator__silhouette-name">${this.escape(product.silhouette)}</span><span class="game-day-configurator__silhouette-price">${money(representativeVariant?.price)}</span></span></button>`;
      }).join('') || '<p class="game-day-configurator__hold">Pick a pairing to see what it comes in.</p>';
      const product = this.product;
      const sizes = [...new Set(product?.variants.map((variant) => variant.options[this.sizeIndex]) || [])].filter((value) => value && value !== 'Default Title');
      this.nodes.sizeList.innerHTML = sizes.map((size) => {
        const variants = product.variants.filter((candidate) => candidate.options[this.sizeIndex] === size);
        const variant = variants.length === 1 ? variants[0] : null;
        const selected = size === this.state.size;
        const soldOut = Boolean(variant && !variant.available);
        const disabled = !variant;
        const escapedSize = size.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
        const firstEnabled = !this.state.size && !disabled && size === sizes.find((candidateSize) => product.variants.some((candidate) => candidate.options[this.sizeIndex] === candidateSize));
        return `<button type="button" class="game-day-configurator__size ${selected ? 'is-selected' : ''} ${soldOut ? 'is-sold-out' : ''}" data-gd-size="${escapedSize}" data-game-day-size="${escapedSize}" role="radio" aria-checked="${selected}" tabindex="${selected || firstEnabled ? '0' : '-1'}"${soldOut ? ` aria-label="${escapedSize}, ${this.escape(this.data.labels.soldOut)}"` : ''}${disabled ? ' disabled aria-disabled="true"' : ''}>${escapedSize}</button>`;
      }).join('');
      const variant = this.variant;
      this.nodes.pairLabel.textContent = pair.title;
      this.nodes.title.textContent = product?.title || 'Choose a silhouette';
      const representativeVariant = product?.variants.find((item) => item.available) || product?.variants[0];
      this.nodes.price.textContent = product ? money((variant || representativeVariant)?.price) : '';
      this.nodes.fitBar.innerHTML = this.renderFitBar(product);
      this.buttonState = computeButtonState(this.canonicalVariant, { hasSelectedSize: Boolean(this.state.size) });
      const lowStock = this.buttonState.state === BUTTON_STATES.ADD_TO_CART && Boolean(variant?.available && variant.inventoryQuantity > 0 && variant.inventoryQuantity <= 3);
      const dispatchDate = this.canonicalVariant?.dispatch_date;
      this.nodes.availability.textContent = this.buttonState.state === BUTTON_STATES.NOTIFY_ME
        ? this.data.labels.unavailable
        : (this.buttonState.state === BUTTON_STATES.PRE_ORDER
          ? `Pre-order - estimated to ship by ${this.formatDispatchDate(dispatchDate)}.`
          : (lowStock ? `Only ${variant.inventoryQuantity} left` : ''));
      this.applyButtonState(this.buttonState, product, variant);
      this.galleryMedia = this.buildGalleryMedia(product, pair);
      this.renderGallery();
      if (this.nodes.pdpLink) { this.nodes.pdpLink.hidden = !product?.url; this.nodes.pdpLink.href = product?.url || '#'; }
      this.syncMobileBar();
      this.applyMobileFolding();
    }

    applyButtonState(buttonState, product, variant) {
      const isNotify = buttonState.state === BUTTON_STATES.NOTIFY_ME;
      const label = {
        [BUTTON_STATES.SELECT_SIZE]: this.data.labels.selectSize,
        [BUTTON_STATES.ADD_TO_CART]: this.data.labels.add,
        [BUTTON_STATES.PRE_ORDER]: this.data.labels.preOrder,
        [BUTTON_STATES.NOTIFY_ME]: this.data.labels.notify,
        [BUTTON_STATES.UNAVAILABLE]: this.data.labels.unavailable
      }[buttonState.state];
      this.nodes.add.disabled = buttonState.disabled;
      this.nodes.add.setAttribute('aria-disabled', String(buttonState.disabled));
      this.nodes.add.dataset.buttonState = buttonState.state;
      this.nodes.add.textContent = label;
      this.nodes.add.classList.toggle('my-bis-button', isNotify);
      if (isNotify && product && variant) {
        this.nodes.add.dataset.productTitle = product.title;
        this.nodes.add.dataset.variantId = variant.id;
        this.nodes.add.dataset.variantTitle = variant.title;
        window.wireNotifyButtons?.();
      } else {
        window.unwireNotifyButton?.(this.nodes.add);
        delete this.nodes.add.dataset.productTitle;
        delete this.nodes.add.dataset.variantId;
        delete this.nodes.add.dataset.variantTitle;
      }
    }

    applyMobileFolding() {
      const mobile = window.matchMedia('(max-width: 700px)').matches;
      const railOne = this.root.querySelector('[data-game-day-rail="one"]');
      const railTwo = this.root.querySelector('[data-game-day-rail="two"]');
      [this.nodes.pairs, this.nodes.silhouettes, this.nodes.sizes].forEach((node) => node.hidden = false);
      railOne.hidden = true;
      railTwo.hidden = true;
      if (!mobile) return;
      this.root.querySelector('[data-gd-receipt-pair]').textContent = this.pair?.title || '';
      this.root.querySelector('[data-gd-receipt-silhouette]').textContent = this.state.silhouette || '';
      this.root.querySelector('[data-gd-receipt-size]').textContent = this.state.size || '';
      this.root.querySelector('[data-gd-receipt-price]').textContent = this.state.size && this.variant ? money(this.variant.price) : '';
      const editing = this.state.editing;
      const editingSilhouette = editing === 'silhouette';
      railOne.hidden = !this.state.committed.pair || editing === 'pair';
      railTwo.hidden = !this.state.committed.silhouette || editingSilhouette;
      this.nodes.pairs.hidden = this.state.committed.pair && editing !== 'pair';
      this.nodes.silhouettes.hidden = this.state.committed.silhouette && !editingSilhouette;
      this.nodes.sizes.hidden = this.state.committed.size && editing !== 'size' && !editingSilhouette;
    }

    advance(target, alignment = 'center') {
      if (!window.matchMedia('(max-width: 700px)').matches) return;
      target?.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: alignment,
        inline: 'nearest'
      });
    }
    buildGalleryMedia(product, pair) {
      const detectedFlatlay = product?.media?.find((media) => media.type === 'image' && media.src && /flat[\s_-]*lay|flatlay|laid\s+flat|^cashmere\s+(?:hoodie|contrast\s+crew)/i.test(`${media.alt || ''} ${media.src}`));
      const flatlaySource = product?.flatlay || detectedFlatlay;
      const flatlay = flatlaySource ? { ...flatlaySource, type: 'image', isFlatlay: true } : null;
      const productMedia = product?.media || (product?.images || []).map((image) => ({ ...image, type: 'image' }));
      const seen = new Set();
      return [flatlay, ...productMedia].filter((media) => {
        const key = media?.src;
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      }).map((media, index, list) => ({
        ...media,
        type: media.type === 'video' ? 'video' : 'image',
        alt: media.alt || (media.isFlatlay ? `${pair.title} ${product.silhouette} flat lay` : `${pair.title} ${product.silhouette} - ${media.type === 'video' ? 'video' : 'photo'} ${index + 1} of ${list.length}`)
      }));
    }

    mediaMarkup(media, eager = false) {
      const alt = this.escape(media.alt || '');
      if (media.type === 'video') {
        const sources = (media.sources || []).filter((source) => safeUrl(source.src)).map((source) => `<source src="${this.escape(safeUrl(source.src))}" type="${this.escape(source.type || '')}">`).join('');
        const fallbackSource = sources ? '' : `<source src="${this.escape(safeUrl(media.src))}" type="video/mp4">`;
        const poster = media.poster ? ` poster="${this.escape(imageUrl(media.poster, 900))}"` : '';
        return `<video controls playsinline preload="metadata"${poster} aria-label="${alt}">${sources || fallbackSource}</video>`;
      }
      return `<img src="${this.escape(imageUrl(media.src, 900))}" alt="${alt}" loading="${eager ? 'eager' : 'lazy'}" width="900" height="980">`;
    }

    renderGallery() {
      if (!this.galleryMedia.length) {
        this.nodes.gallery.innerHTML = '<div class="game-day-configurator__gallery-main"><p class="game-day-configurator__hold">Pick a pairing to see what it comes in.</p></div>';
        return;
      }
      const rail = this.galleryMedia.map((media, index) => {
        const preview = media.type === 'video' ? media.poster : media.src;
        const thumbnail = preview ? `<img src="${this.escape(imageUrl(preview, 150))}" alt="" loading="${index ? 'lazy' : 'eager'}" width="150" height="164">` : '<span aria-hidden="true">Video</span>';
        return `<button type="button" data-gd-gallery-index="${index}" aria-pressed="${index === 0}" aria-label="View ${this.escape(media.alt)}">${thumbnail}</button>`;
      }).join('');
      const caption = `${this.escape(this.pair.title)} &mdash; ${this.escape(this.product.silhouette)}`;
      this.nodes.gallery.innerHTML = `<div class="game-day-configurator__gallery-main">${this.mediaMarkup(this.galleryMedia[0], true)}</div><div class="game-day-configurator__gallery-caption"><span class="game-day-configurator__gallery-caption-name">${caption}</span><span class="game-day-configurator__gallery-caption-progress">Your build so far</span></div><div class="game-day-configurator__gallery-rail" data-game-day-rail>${rail}</div>`;
    }

    selectGalleryMedia(index) {
      const media = this.galleryMedia[index];
      const main = this.nodes.gallery.querySelector('.game-day-configurator__gallery-main');
      if (!media || !main) return;
      main.innerHTML = this.mediaMarkup(media);
      this.nodes.gallery.querySelectorAll('[data-gd-gallery-index]').forEach((button) => button.setAttribute('aria-pressed', String(Number(button.dataset.gdGalleryIndex) === index)));
    }

    formatDispatchDate(dispatchDate) {
      const date = new Date(`${dispatchDate}T12:00:00`);
      if (Number.isNaN(date.getTime())) return dispatchDate;
      return date.toLocaleDateString(document.documentElement.lang || 'en-US', { month: 'long', day: 'numeric' });
    }
    escape(value) { const div = document.createElement('div'); div.textContent = value; return div.innerHTML; }

    async add() {
      const variant = this.variant;
      if (!variant || this.nodes.add.getAttribute('aria-busy') === 'true') return;
      if (this.buttonState?.state === BUTTON_STATES.NOTIFY_ME) { window.wireNotifyButtons?.(); return; }
      if (this.buttonState?.disabled) return;
      const selectionRevision = this.selectionRevision;
      const variantId = variant.id;
      const state = this.buttonState.state;
      const requestId = ++this.activeAddRequestId;
      this.nodes.error.hidden = true;
      this.nodes.add.setAttribute('aria-busy', 'true');
      this.syncMobileBar();
      const label = this.nodes.add.textContent;
      this.nodes.add.textContent = this.data.labels.adding;
      try {
        const sections = [];
        document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));
        const formData = new FormData();
        formData.set('id', variant.id);
        formData.set('quantity', '1');
        if (this.buttonState?.preOrderProperty) formData.set('properties[Pre-order]', this.buttonState.preOrderProperty);
        formData.set('sections', sections.join(','));
        formData.set('sections_url', window.location.pathname);
        const response = await fetch(`${this.data.rootUrl}cart/add.js`, { method: 'POST', headers: { 'X-Requested-With': 'XMLHttpRequest' }, body: formData });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload.description || this.data.labels.error);
        const cartResponse = await fetch(`${this.data.rootUrl}cart.js`);
        const cart = await cartResponse.json().catch(() => null);
        if (!cartResponse.ok || !cart) throw new Error(this.data.labels.error);
        if (this.selectionRevision !== selectionRevision || this.variant?.id !== variantId || this.buttonState?.state !== state) {
          this.render();
          return;
        }
        cart.sections = payload.sections;
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', onSuccessDo: 'force_open_drawer', cart } }));
        this.nodes.add.textContent = this.data.labels.added;
        this.nodes.add.dataset.added = 'true';
        this.syncMobileBar();
        if (sections.length === 0) window.location.assign(`${this.data.rootUrl}cart`);
      } catch (error) {
        if (this.selectionRevision !== selectionRevision || this.variant?.id !== variantId || this.buttonState?.state !== state) {
          this.render();
          return;
        }
        this.nodes.error.textContent = error.message || this.data.labels.error;
        this.nodes.error.hidden = false;
        this.nodes.add.textContent = label;
      } finally {
        if (this.activeAddRequestId === requestId) {
          this.nodes.add.removeAttribute('aria-busy');
          this.syncMobileBar();
        }
      }
    }
  }

  const rootsWithin = (scope) => [scope?.matches?.(SELECTOR) ? scope : null, ...Array.from(scope?.querySelectorAll?.(SELECTOR) || [])].filter(Boolean);
  const initialize = (scope = document) => rootsWithin(scope).forEach((root) => new GameDayConfigurator(root));
  window.PuraGameDayConfigurator = { initialize, computeButtonState, formatPreOrderProperty, isPreOrderActive, BUTTON_STATES };
  document.addEventListener('DOMContentLoaded', () => initialize());
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => rootsWithin(event.target).forEach((root) => root.__gameDayConfigurator?.destroy()));
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-game-day-silhouette-preference]');
    if (!trigger) return;
    document.querySelector(SELECTOR)?.__gameDayConfigurator?.setSilhouettePreference(trigger.dataset.gameDaySilhouettePreference);
  });
})();
